import { useState, useRef, useEffect } from 'react';
import './index.css';

// ===== 高德 JS API 单例加载（不渲染地图，仅用 AutoComplete 插件做地点检索）=====
// key 与 map-select 页面共用，避免重复申请
const AMAP_KEY = 'ab7068ca37ab9cc5d2efc66efb691223';
const AMAP_SEC_CODE = 'a8fc2f3553e82bb29efb33154dbecd0d';
let amapLoadingPromise = null;

function loadAMapAutoComplete() {
    if (window.AMap && window.AMap.AutoComplete) {
        return Promise.resolve(window.AMap);
    }
    if (amapLoadingPromise) {
        return amapLoadingPromise;
    }
    amapLoadingPromise = new Promise((resolve, reject) => {
        window._AMapSecurityConfig = { securityJsCode: AMAP_SEC_CODE };
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://webapi.amap.com/maps?v=2.0&key=${AMAP_KEY}`;
        script.onload = () => {
            const AMap = window.AMap;
            if (!AMap) {
                amapLoadingPromise = null;
                reject(new Error('AMap对象未找到'));
                return;
            }
            AMap.plugin('AMap.AutoComplete', () => resolve(AMap));
        };
        script.onerror = () => {
            amapLoadingPromise = null;
            reject(new Error('高德地图API加载失败'));
        };
        document.head.appendChild(script);
    });
    return amapLoadingPromise;
}

/**
 * 可内联地图检索的地点输入框（无地图渲染）
 *
 * - 输入防抖 300ms 后调高德 AutoComplete，下拉展示建议（名称+区域地址）
 * - 点选建议 → onChange(名称) + onSelect({name, address, lat, lng})
 * - 不点建议直接输入 → 文本原样保留（与原有"手动输入"行为一致）
 *
 * @param {string}   value           受控值（地点名称文本）
 * @param {Function} onChange        (text: string) => void
 * @param {Function} onSelect        可选，(poi: {name,address,lat,lng}) => void
 * @param {string}   inputClassName  透传给内部 input，复用页面既有输入框样式
 * @param {string}   wrapperClassName 附加到组件根节点（默认已有 .lsi）
 * @param {object}   style           组件根节点内联样式（如 flex:1）
 * @param {object}   rest            其余 props 透传给 input（required/onKeyPress/name 等）
 */
function LocationSearchInput({
    value,
    onChange,
    onSelect,
    placeholder,
    inputClassName,
    wrapperClassName,
    style,
    ...rest
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const autoCompleteRef = useRef(null);
    const wrapRef = useRef(null);
    const debounceTimer = useRef(null);
    const searchSeq = useRef(0);

    // 挂载时加载高德 SDK + AutoComplete 插件
    useEffect(() => {
        let mounted = true;
        loadAMapAutoComplete()
            .then((AMap) => {
                if (!mounted) return;
                autoCompleteRef.current = new AMap.AutoComplete({
                    city: '全国',
                    citylimit: false
                });
            })
            .catch((e) => {
                console.error('[LocationSearchInput] 检索初始化失败:', e.message);
            });
        return () => { mounted = false; };
    }, []);

    // 点击组件外部收起下拉
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, []);

    // 卸载清理防抖定时器
    useEffect(() => () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }, []);

    const handleInputChange = (e) => {
        const text = e.target.value;
        onChange && onChange(text);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const keyword = text.trim();
        if (!keyword) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        debounceTimer.current = setTimeout(() => {
            const ac = autoCompleteRef.current;
            if (!ac) return;
            const seq = ++searchSeq.current;
            setSearching(true);
            ac.search(keyword, (status, result) => {
                if (seq !== searchSeq.current) return; // 过期响应丢弃
                setSearching(false);
                if (status === 'complete' && result.tips) {
                    const list = result.tips
                        .filter(t => t && t.name && (t.location || (typeof t.district === 'string' && t.district)))
                        .slice(0, 8);
                    setSuggestions(list);
                    setOpen(list.length > 0);
                } else {
                    setSuggestions([]);
                    setOpen(false);
                }
            });
        }, 300);
    };

    // 点选建议：onMouseDown + preventDefault 抢在 input blur 之前，避免下拉先被收起
    const handlePick = (tip) => {
        const district = typeof tip.district === 'string' ? tip.district : '';
        const address = typeof tip.address === 'string' && tip.address && tip.address !== '[]' ? tip.address : '';
        const poi = {
            name: tip.name,
            address: district ? `${district}${address ? ` ${address}` : ''}` : (address || '未知地址'),
            lat: tip.location ? tip.location.lat : 0,
            lng: tip.location ? tip.location.lng : 0
        };
        onChange && onChange(tip.name);
        onSelect && onSelect(poi);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div className={`lsi${wrapperClassName ? ` ${wrapperClassName}` : ''}`} ref={wrapRef} style={style}>
            <input
                type="text"
                className={inputClassName}
                value={value}
                onChange={handleInputChange}
                onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
                placeholder={placeholder}
                autoComplete="off"
                {...rest}
            />
            {searching && <span className="lsi-loading" />}
            {open && suggestions.length > 0 && (
                <ul className="lsi-dropdown">
                    {suggestions.map((tip, i) => {
                        const district = typeof tip.district === 'string' ? tip.district : '';
                        const address = typeof tip.address === 'string' && tip.address && tip.address !== '[]' ? tip.address : '';
                        return (
                            <li
                                key={i}
                                className="lsi-option"
                                onMouseDown={(e) => { e.preventDefault(); handlePick(tip); }}
                            >
                                <span className="lsi-option-name">{tip.name}</span>
                                {(district || address) && (
                                    <span className="lsi-option-addr">{district}{address ? ` ${address}` : ''}</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default LocationSearchInput;
